import React, { FC, useEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KTooltip, KSlider } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { users } from "../../../../../apis"
import { User, UserAdvanceProgress, UserAdvanceTotals } from "../../../../../types"
import {
  addCommasToNumber,
  calculateContractEnds,
  mapListToCamelCase,
  mapToCamelCase,
  monthsBetween,
  replaceCurrencies
} from "../../../../../utility"
import Pagination from "../../../../ui/table/Pagination"
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js"
import { Line } from "react-chartjs-2"
import SearchUserComponent from "../../../../ui/components/SearchUserComponent"
import ToggleInput from "../../../../ui/components/ToggleInput"
import {
  currencyIdNames,
  currencyOptions,
  errorFilledToastOptions,
  infoFilledToastOptions,
  successFilledToastOptions
} from "../../../../../constants"
import { toast } from "react-toastify"
import { exportUserAdvanceProgress } from "./downloadAdvancesCSV"
import { AdvanceRecoupment, calculateAdvanceRecoupment } from "./advanceRecoupment"
import PlatformCompletenessInfo, { recentMonths } from "../../../../ui/components/PlatformCompletenessInfo"

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend)

const RoyaltyDateOptionList: KSelectOption[] = [
  { label: "Activity Period", value: 1, value2: "activity_period" },
  { label: "Reporting Period", value: 2, value2: "reporting_period" }
]

const IRROptionList: KSelectOption[] = [
  { label: "IRR Net (Default)", value: 1, value2: "irr_net" },
  { label: "IRR Gross", value: 2, value2: "irr_gross" }
]

const ViewOptionList: KSelectOption[] = [
  { label: "Kahuna Share", value: 1, value2: "kahuna_share" },
  { label: "Royalties", value: 2, value2: "royalties" }
]

const AdvanceDateOptionList: KSelectOption[] = [
  { label: "Advance Start (Activity Period)", value: 1, value2: "start_date" },
  { label: "Advance Paid", value: 2, value2: "end_date" }
]

const RoyaltyTypeOptionList: KSelectOption[] = [
  { label: "Total Amount (Gross)", value: 1, value2: "total_amount" },
  { label: "Total Net (Artist Share)", value: 2, value2: "total_net" }
]

const UserTypeOptionList: KSelectOption[] = [
  { label: "User", value: 1, value2: "user" },
  { label: "Total", value: 2, value2: "total" }
]

const RoyaltiesMonths: FC<{
  userId: number
  royalties: { [key: string]: number }
  recoupedMonthTracker: { [key: string]: boolean }
  currency: string
  timelineActive: boolean
  reportingPeriod: boolean
}> = ({ userId, royalties, recoupedMonthTracker, currency, timelineActive, reportingPeriod }) => {
  const monthList = Object.keys(royalties).sort()
  const completenessMonths = recentMonths(monthList)

  return (
    <div className="flex pb-4" style={{ width: "100%" }}>
      {monthList.map((month, index) => (
        <div key={`royalty_${month}`} className="grid gap-1 flex-shrink-0" style={{ width: 100 }}>
          <div className="flex items-center gap-1">
            <KSpan
              color={recoupedMonthTracker[month] ? "green" : "black"}
              text={timelineActive ? month : `M${index}`}
            />
            {completenessMonths.includes(month) && (
              <PlatformCompletenessInfo userId={userId} months={[month]} reportingPeriod={reportingPeriod} />
            )}
          </div>
          <KSpan
            color={recoupedMonthTracker[month] ? "green" : "black"}
            text={`${replaceCurrencies(currency)} ${addCommasToNumber(royalties[month].toFixed(2))} `}
          />
        </div>
      ))}
    </div>
  )
}

type AdminUserAdvanceProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const AdminUserAdvance: FC<AdminUserAdvanceProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userAdvanceProgressList, setUserAdvanceProgressList] = useState<UserAdvanceProgress[]>([])
  const [count, setCount] = useState(0)
  const [selectedAdvanceDateType, setSelectedAdvanceDateType] = useState<KSelectOption>(AdvanceDateOptionList[1])
  const [selectedSummaryDateType, setSelectedSummaryDateType] = useState<KSelectOption>(RoyaltyDateOptionList[1])
  const [selectedViewOption, setSelectedViewOption] = useState<KSelectOption>(ViewOptionList[0])
  const [selectedIRROption, setSelectedIRROption] = useState<KSelectOption>(IRROptionList[0])
  const [selectedCurrency, setSelectedCurrency] = useState<KSelectOption>(currencyOptions[3])
  const [selectedRoyaltyType, setSelectedRoyaltyType] = useState<KSelectOption>(RoyaltyTypeOptionList[0])
  const [showExcluded, setShowExcluded] = useState<boolean>(false)
  const [advancePaidOut, setAdvancePaidOut] = useState<boolean>(false)
  const [timelineActive, setTimelineActive] = useState<boolean>(true)
  const [selectedUserType, setSelectedUserType] = useState<KSelectOption>(UserTypeOptionList[0])
  const [totals, setTotals] = useState<UserAdvanceTotals>()
  const [contractOpenList, setContractOpenList] = useState<number[]>([])
  const [aggragateOpenList, setAggragateOpenList] = useState<number[]>([])

  const fetchUserAdvanceProgress = (offset: number) => {
    setLoading(true)
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    users(
      `/user_advance_progress/?offset=${offset}&limit=${limit}${userIdParam}&royalty_type=${
        selectedRoyaltyType.value2
      }&user_type=${selectedUserType.value2}&date_type=${selectedAdvanceDateType.value2}&summary_date_key=${
        selectedSummaryDateType.value2
      }&view_option=${selectedViewOption.value2}&irr_option=${
        selectedIRROption.value2
      }&show_excluded=${showExcluded}&currency=${selectedCurrency.value2}&timeline_active=${
        timelineActive ? "1" : "0"
      }&advance_paid_out=${advancePaidOut ? "1" : "0"}`,
      "GET"
    )
      .then((resp) => {
        setUserAdvanceProgressList(mapListToCamelCase(resp.data.results))
        const totals: UserAdvanceTotals = mapToCamelCase(resp.data.totals)
        setTotals(totals)
        setCount(resp.data.count)
        setLoading(false)
      })
      .catch(() => {
        toast.error("Failed to fetch user advance progress data.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const applyFilters = () => {
    setOffset(0) // reset to first page when filters change
    fetchUserAdvanceProgress(0)
  }

  const exportAll = () => {
    if (!window.confirm("Are you sure you want to export the current view as CSV?")) {
      return
    }

    setDownloadLoading(true)
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    users(
      `/user_advance_progress/?offset=0&limit=1000${userIdParam}&royalty_type=${selectedRoyaltyType.value2}&user_type=${selectedUserType.value2}&date_type=${selectedAdvanceDateType.value2}&summary_date_key=${selectedSummaryDateType.value2}&view_option=${selectedViewOption.value2}&irr_option=${selectedIRROption.value2}&show_excluded=${showExcluded}&currency=${selectedCurrency.value2}`,
      "GET"
    )
      .then((resp) => {
        toast.info(`Starting XLSXL download for ${resp.data.count} records...`, infoFilledToastOptions)
        exportUserAdvanceProgress(mapListToCamelCase(resp.data.results), timelineActive)
        setDownloadLoading(false)
        toast.info(`Finished XLSXL download`, successFilledToastOptions)
      })
      .catch(() => {
        setDownloadLoading(false)
        toast.error("Failed to export.", errorFilledToastOptions)
      })
  }

  const onClickPagination = (pageNumber: number) => {
    const offset = (pageNumber - 1) * limit
    setOffset(offset)
    fetchUserAdvanceProgress(offset)
  }

  const renderEstimatedRecoupment = (recoupment: AdvanceRecoupment | undefined, recoupMonthTotal: number) => {
    // Advance can not be recouped within the projection horizon, fall back to the overall estimate
    if (!recoupment || recoupment.estimatedMonths === null || recoupment.totalMonths === null) {
      return (
        <div>
          <KSpan text={`Total Recoup: ${recoupMonthTotal} months`} />
        </div>
      )
    }

    const label = `Est. / Total Recoup: ${recoupment.estimatedMonths} / ${recoupment.totalMonths} months`

    // Sequential breakdown, e.g. 8 (7+1): 7 months for the previous advance, 1 more for this one
    const estimatedParts = recoupment.estimatedParts.filter((part) => part !== 0)
    const totalParts = recoupment.totalParts.filter((part) => part !== 0)

    // Only the advances following the first one have a breakdown to show
    if (estimatedParts.length < 2 && totalParts.length < 2) {
      return (
        <div>
          <KSpan text={label} />
        </div>
      )
    }

    return (
      <KTooltip
        backgroundColor="#000"
        padding="4px 8px"
        arrowColor="#000"
        position="bottom"
        content={
          <div className="w-max grid gap-1">
            <KSpan
              fontSize={11}
              color="#FFF"
              text={`Est. Recoup: ${recoupment.estimatedMonths}${
                estimatedParts.length > 1 ? ` (${estimatedParts.join("+")})` : ""
              } months`}
            />
            <KSpan
              fontSize={11}
              color="#FFF"
              text={`Total Recoup: ${recoupment.totalMonths}${
                totalParts.length > 1 ? ` (${totalParts.join("+")})` : ""
              } months`}
            />
            <KSpan fontSize={11} color="#FFF" text={`Recoup completes at ${recoupment.completionMonth}`} />
          </div>
        }
      >
        <div className="w-max">
          <KSpan text={label} />
        </div>
      </KTooltip>
    )
  }

  const renderIRRMetrics = (irrMonthly: number, irr12M: number, irr24M: number, irr36M: number, className: string) => {
    return (
      <div className={`mt-6 flex ${className}`}>
        <KTitleSpan fontSize={14} lineHeight="18px" text="IRR Metrics:" />
        <KSpan text={`Monthly IRR: ${irrMonthly ? `${(irrMonthly * 100).toFixed(2)}%` : "N/A"}`} />
        <KTooltip
          backgroundColor="#000"
          padding="4px 8px"
          arrowColor="#000"
          position="right"
          content={
            <div className="w-max">
              <KSpan text="Forecast: Based on 12 months of projections" fontSize={11} color="#FFF" />
            </div>
          }
        >
          <KSpan text={`12M IRR: ${irr12M ? `${(irr12M * 100).toFixed(2)}%` : "N/A"}`} color="#4F46E5" />
        </KTooltip>
        <KTooltip
          backgroundColor="#000"
          padding="4px 8px"
          arrowColor="#000"
          position="right"
          content={
            <div className="w-max">
              <KSpan text="Forecast: Based on 24 months of projections" fontSize={11} color="#FFF" />
            </div>
          }
        >
          <KSpan text={`24M IRR: ${irr24M ? `${(irr24M * 100).toFixed(2)}%` : "N/A"}`} color="#4F46E5" />
        </KTooltip>
        <KTooltip
          backgroundColor="#000"
          padding="4px 8px"
          arrowColor="#000"
          position="right"
          content={
            <div className="w-max">
              <KSpan text="Projection is a long-term scenario, not a forecast (36 months)" fontSize={11} color="#FFF" />
            </div>
          }
        >
          <KSpan text={`36M IRR: ${irr36M ? `${(irr36M * 100).toFixed(2)}%` : "N/A"}`} color="#FF9800" />
        </KTooltip>
      </div>
    )
  }

  const onUpdateExcludeFromProjection = (user: User) => {
    if (!user) {
      return
    }
    users(`/update_exclude_projection/`, "PUT", {
      user_id: user.id,
      exclude_from_projection: !user.excludeFromProjection
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error("Failed to update projection exclusion status", errorFilledToastOptions)
        return
      }
      toast.success("Projection exclusion status updated successfully", successFilledToastOptions)
      fetchUserAdvanceProgress(offset)
    })
  }

  const onClickContract = (userId: number) => {
    if (contractOpenList.includes(userId)) {
      setContractOpenList(contractOpenList.filter((id) => id !== userId))
    } else {
      setContractOpenList([...contractOpenList, userId])
    }
  }

  const onClickAggragate = (userId: number) => {
    if (aggragateOpenList.includes(userId)) {
      setAggragateOpenList(aggragateOpenList.filter((id) => id !== userId))
    } else {
      setAggragateOpenList([...aggragateOpenList, userId])
    }
  }

  const renderOfferContractDetail = (userProgress: UserAdvanceProgress) => {
    return (
      <div className="flex flex-col" style={{ width: "300px" }}>
        <div>
          <KSpan fontWeight={500} fontSize={16} color="#111" text="Offer Detail" />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Admin Fee: " />
          <KSpan text={userProgress?.userExpense?.adminFee?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="New Release Pre Commission: " />
          <KSpan text={userProgress?.userExpense?.distributionFee?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="New Release Post Commission: " />
          <KSpan text={userProgress?.userExpense?.distributionFeePostRecoup?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Takedown Pre Recoup Commission: " />
          <KSpan text={userProgress?.userExpense?.preRecoupCommission?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Takedown Post Recoup Commission:" />
          <KSpan text={userProgress?.userExpense?.postRecoupCommission?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Preferred Currency: " />
          <KSpan text={currencyIdNames(parseInt(userProgress?.userExpense?.preferredCurrency?.toString() || "0"))} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Country: " />
          <KSpan text={userProgress?.userInformation?.country?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Signed At: " />
          <KSpan text={userProgress?.dealOffer?.signedAt?.substring(0, 10) || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Contract Ends:" />
          <KSpan text={calculateContractEnds(userProgress?.dealOffer)} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Type: " />
          <KSpan text={userProgress?.dealOffer?.contractModel || "-"} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Length: " />
          <KSpan
            text={
              userProgress?.dealOffer?.contractModel === "post_recoup"
                ? userProgress?.dealOffer?.postRecoupDuration?.toString() || "-"
                : userProgress?.dealOffer?.contractDuration?.toString() || "-"
            }
          />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Included in Recoupment: " />
          <KSpan text={userProgress?.dealOffer?.newReleaseIncludeRecoup?.toString() || ""} />
        </div>
        <div className="flex gap-2 mt-2">
          <KSpan text="Exclusivity: " />
          <KSpan text={userProgress?.dealOffer?.contractExclusivity?.toString() || ""} />
        </div>
      </div>
    )
  }

  const renderAggragateRow = (
    royalties: { [key: string]: number },
    projections: { [key: string]: number },
    advances: { [key: string]: number },
    header: string,
    isCumulative: boolean,
    color: boolean,
    currency: string
  ) => {
    const cashflowMap: Record<string, number> = {}

    // 1. Add royalties (positive)
    let firstMonth = ""
    Object.entries(royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) {
        firstMonth = month
      }
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 2. Add projections (positive)
    Object.entries(projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 3. Add advances (negative)
    Object.entries(advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })

    // 4. Convert to sorted array format
    const cashflow: Record<string, number> = {}
    let currentCash = 0

    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        if (isCumulative) {
          currentCash += cashflowMap[month]
          cashflow[month] = currentCash
        } else {
          cashflow[month] = cashflowMap[month]
        }
      })

    return (
      <div className="flex pt-1" style={{ borderTop: "1px solid grey" }}>
        <div style={{ maxWidth: 380, minWidth: 380 }}>
          <KSpan text={header} />
        </div>
        {Object.entries(cashflow).map(([month, amount]) => {
          return (
            <div key={month} style={{ maxWidth: 115, minWidth: 115 }}>
              <KSpan
                color={color ? (amount >= 0 ? "#35ba00" : "#f76868") : ""}
                text={`${replaceCurrencies(currency)} ${addCommasToNumber(amount.toFixed(2))}`}
              />
            </div>
          )
        })}
      </div>
    )
  }

  const renderAggragateTable = (
    userProgress: UserAdvanceProgress,
    royalties: { [key: string]: number },
    recoupedMonthTracker: { [key: string]: boolean }
  ) => {
    const monthList: string[] = []
    Object.keys(userProgress.actualGrossAndProjection?.royalties || {}).forEach((month) => {
      monthList.push(month)
    })
    Object.keys(userProgress.actualGrossAndProjection?.projections || {}).forEach((month) => {
      if (!monthList.includes(month)) monthList.push(month)
    })
    return (
      <div className="mt-8 flex flex-col gap-2" style={{ maxWidth: "100%" }}>
        <div className="flex">
          <div style={{ maxWidth: 380, minWidth: 380 }} />
          {monthList.map((month, index) => (
            <div key={`aggragate_${month}`} style={{ maxWidth: 115, minWidth: 115 }}>
              <KSpan
                color={recoupedMonthTracker[month] ? "green" : "black"}
                fontWeight={500}
                text={timelineActive ? month : `M${index}`}
              />
            </div>
          ))}
        </div>
        {renderAggragateRow(
          userProgress.actualGrossAndProjection?.royalties,
          userProgress.actualGrossAndProjection?.projections,
          userProgress.actualGrossAndProjection?.advances,
          "Gross Collections",
          false,
          false,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.kahunaRevenue?.royalties,
          userProgress.kahunaRevenue?.projections,
          userProgress.kahunaRevenue?.advances,
          "Kahuna Revenue",
          false,
          false,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.kahunaAdvanceCollection?.royalties,
          userProgress.kahunaAdvanceCollection?.projections,
          userProgress.kahunaAdvanceCollection?.advances,
          "Advance Recoup Applied",
          false,
          false,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.artistPayable?.royalties,
          userProgress.artistPayable?.projections,
          userProgress.artistPayable?.advances,
          "Artist Payable",
          false,
          false,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.advancesTimeline,
          {},
          {},
          "New Advance Funded",
          false,
          false,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.artistBalance?.royalties,
          userProgress.artistBalance?.projections,
          userProgress.artistBalance?.advances,
          "Outstanding Advance Balance (EoM)",
          false,
          true,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.kahunaTotal?.royalties,
          userProgress.kahunaTotal?.projections,
          userProgress.kahunaTotal?.advances,
          "Kahuna Net Cash Flow for IRR",
          false,
          false,
          userProgress.currency
        )}
        {renderAggragateRow(
          userProgress.royalties,
          userProgress.projections,
          userProgress.advances,
          "Kahuna Balance",
          true,
          true,
          userProgress.currency
        )}
      </div>
    )
  }

  const renderUserAdvance = () => {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {userAdvanceProgressList.map((userProgress) => {
          const {
            user,
            advances,
            royalties,
            currency,
            irrAnnual,
            irrMonthly,
            irr12M,
            irr24M,
            irr36M,
            projections,
            recoupMonthTotal,
            advancesTimeline
          } = userProgress

          // Recoupment of every advance, calculated as one sequential (FIFO) process
          const { advanceMap, recoupedMonthTracker, completionMonth, completionTotalMonths, completionRecouped } =
            calculateAdvanceRecoupment(userProgress)

          let advanceTotal = 0
          let royaltyTotal = 0
          Object.entries(royalties).forEach(([royaltyMonth, royaltyAmount]) => {
            royaltyTotal += royaltyAmount
          })
          Object.entries(advances).forEach(([advMonth, advAmount]) => {
            advanceTotal += advAmount
          })

          const allMonths = Object.keys({ ...advances, ...royalties, ...projections }).sort()

          const realMonths = Object.keys({ ...advances, ...royalties }).sort()
          const projectionMonths = Object.keys(projections).sort()

          let cumulative = 0
          const realChartData: number[] = []

          realMonths.forEach((month) => {
            const royalty = royalties[month] || 0
            const advance = advances[month] || 0
            cumulative += royalty - advance
            realChartData.push(cumulative)
          })

          const projectionChartData: number[] = []

          projectionMonths.forEach((month) => {
            const projValue = projections[month] || 0
            cumulative += projValue // continue cumulative forward
            projectionChartData.push(cumulative)
          })

          const labels = timelineActive
            ? [...realMonths, ...projectionMonths]
            : [...realMonths, ...projectionMonths].map((_, index) => `M${index}`)

          // 1️⃣ Pad projectionChartData so it aligns after real months
          const paddedProjectionData = [
            ...Array(realMonths.length).fill(null), // fill the beginning with nulls
            ...projectionChartData // actual projection values
          ]

          // 2️⃣ Real data stays as is
          const paddedRealData = [
            ...realChartData,
            ...Array(projectionMonths.length).fill(null) // pad after last real month if needed
          ]

          const data = {
            labels,
            datasets: [
              {
                label: "Actual Balance (Historical)",
                data: paddedRealData,
                borderColor: "#4F46E5",
                segment: {
                  backgroundColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "rgba(24, 154, 35, 0.3)" : "rgba(107,114,128,0.3)"
                  },
                  borderColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "#189A23" : "#6B6E80"
                  }
                },
                fill: true,
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6
              },
              {
                label: "Projection (36M Forecast)",
                data: paddedProjectionData,
                borderColor: "#FF9800",
                fill: true,
                borderDash: [6, 4],
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6,
                segment: {
                  backgroundColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "rgba(24, 154, 35, 0.3)" : "rgba(107,114,128,0.3)"
                  },
                  borderColor: (ctx: any) => {
                    const startValue = ctx.p1.parsed.y
                    return startValue >= 0 ? "#189A23" : "#6B6E80"
                  }
                }
              }
            ]
          }

          const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: "top" as const,
                labels: {
                  usePointStyle: true,
                  padding: 15,
                  font: { size: 11 }
                }
              },
              tooltip: { mode: "index" as const, intersect: false },
              datalabels: { display: false }
            },
            scales: {
              x: {
                title: { display: false, text: "Month" }
              },
              y: {
                title: { display: false, text: "Amount" },
                beginAtZero: true
              }
            }
          }

          return (
            <div
              key={`user_advance_progress_${userProgress.userId}`}
              className="flex flex-col gap-4"
              style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "16px",
                opacity: userProgress.newUser || userProgress.user?.excludeFromProjection ? 0.5 : 1,
                backgroundColor: userProgress.newUser || userProgress.user?.excludeFromProjection ? "#d6d6d6" : ""
              }}
            >
              {/* User Info + Advances + Royalties */}
              <div className="flex" style={{ width: "100%" }}>
                {/* User Info */}
                <div style={{ width: "350px" }}>
                  {selectedUserType.value2 === "user" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                      }}
                    >
                      <img
                        src={user?.photo || "/account_icons/avatar.svg"}
                        alt={user?.firstName}
                        style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                      />
                      <div>
                        <div>
                          <KTitleSpan fontSize={16} lineHeight="20px" text={`${user?.firstName} ${user?.lastName}`} />
                        </div>
                        <div>
                          <KSpan fontSize={12} text={user?.email} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Advances */}
                  <div style={{ marginTop: "12px" }}>
                    <div className="flex gap-2 mb-3">
                      <KTitleSpan fontSize={16} lineHeight="20px" text="Contract:" />
                      <img
                        className="cursor-pointer"
                        src="/navigation_icons/profile_dropdownmenu_icons/sign-contract.svg"
                        onClick={() => onClickContract(userProgress.userId)}
                      />
                    </div>
                    <div>
                      <KTitleSpan fontSize={16} lineHeight="20px" text="Advances:" />
                    </div>
                    {Object.entries(timelineActive ? advances : advancesTimeline)
                      .filter(([_, value]) => value > 0)
                      .map(([month, amount]) => (
                        <div
                          className={selectedUserType.value2 === "user" ? "mb-6" : "mb-1"}
                          key={`${userProgress.userId}_${month}_${"advance"}`}
                        >
                          <div
                            key={`advance_${month}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "4px"
                            }}
                          >
                            <div>
                              <KSpan text={`${month}:`} />
                            </div>
                            <div>
                              <KSpan text={`${replaceCurrencies(currency)} ${addCommasToNumber(amount.toFixed(2))}`} />
                            </div>
                            {selectedUserType.value2 === "user" && (
                              <div
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                  backgroundColor: advanceMap[month]?.recouped ? "green" : "red"
                                }}
                              />
                            )}
                          </div>
                          {selectedUserType.value2 === "user" && (
                            <div className="mt-1">
                              {advanceMap[month]?.recouped ? (
                                <KSpan
                                  color="green"
                                  text={`Recouped: ${monthsBetween(month, advanceMap[month].completionMonth)} months`}
                                />
                              ) : (
                                <div className="grid gap-1">
                                  <KSpan
                                    text={`Remaining: ${replaceCurrencies(currency)} ${addCommasToNumber(
                                      (advanceMap[month]?.remaining ?? 0).toFixed(2)
                                    )} `}
                                  />
                                  {renderEstimatedRecoupment(advanceMap[month], userProgress.recoupMonthTotal)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    {selectedUserType.value2 === "user" && completionMonth && completionTotalMonths !== null && (
                      <div className="mt-2">
                        <KSpan
                          fontWeight={500}
                          color={completionRecouped ? "green" : "#111"}
                          text={`Recoup ${
                            completionRecouped ? "completed" : "completes"
                          } at ${completionMonth}: ${completionTotalMonths} months`}
                        />
                      </div>
                    )}
                  </div>
                  {selectedUserType.value2 !== "total" &&
                    renderIRRMetrics(irrMonthly, irr12M, irr24M, irr36M, "flex-col gap-1")}
                </div>
                {contractOpenList.includes(userProgress.userId) && renderOfferContractDetail(userProgress)}

                {/* Royalties */}
                <div
                  className="ml-12"
                  style={{ width: contractOpenList.includes(userProgress.userId) ? "65%" : "80%" }}
                >
                  <div className="mb-2 flex gap-4 items-center">
                    <KTitleSpan fontSize={16} lineHeight="20px" text="Royalties" />
                    {userProgress.newUser && (
                      <div>
                        <KSpan text="(New User)" />
                      </div>
                    )}
                    {userProgress.user && (
                      <div className="ml-32 items-center flex gap-2">
                        <ToggleInput
                          checked={userProgress.user?.excludeFromProjection}
                          handleChange={() => onUpdateExcludeFromProjection(userProgress.user)}
                        />
                        <KSpan text="Exclude from projection" />
                      </div>
                    )}
                  </div>
                  <RoyaltiesMonths
                    userId={userProgress.userId}
                    royalties={royalties}
                    recoupedMonthTracker={recoupedMonthTracker}
                    currency={currency}
                    timelineActive={timelineActive}
                    reportingPeriod={selectedSummaryDateType.value2 === "reporting_period"}
                  />
                  {/* Chart.js Line Chart */}
                  <div className="pt-4" style={{ width: "100%", height: 250 }}>
                    <Line data={data} options={options} />
                  </div>
                  <div className="pt-8 flex gap-12">
                    <div
                      className="cursor-pointer"
                      onClick={() => onClickAggragate(userProgress.userId)}
                      style={{ marginTop: -4 }}
                    >
                      <KSpan
                        text={aggragateOpenList.includes(userProgress.userId) ? "Hide Aggregate" : "Show Aggregate"}
                      />
                    </div>
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`Advance Total: ${replaceCurrencies(currency)} ${addCommasToNumber(
                        advanceTotal.toFixed(2)
                      )} `}
                    />
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`Royalty Total: ${replaceCurrencies(currency)} ${addCommasToNumber(
                        royaltyTotal.toFixed(2)
                      )} `}
                    />
                    <KSpan
                      fontWeight={500}
                      color="#111"
                      text={`Remaining Total: ${
                        royaltyTotal < advanceTotal
                          ? `${replaceCurrencies(currency)} ${addCommasToNumber(
                              (advanceTotal - royaltyTotal).toFixed(2)
                            )} `
                          : "-"
                      }`}
                    />
                  </div>
                  {selectedUserType.value2 === "total" && totals && (
                    <div className="pt-2 flex gap-12">
                      <KSpan fontWeight={500} color="#111" text={`# Advance: ${totals.advanceCount}`} />
                      <KSpan
                        fontWeight={500}
                        color="#111"
                        text={`Avg Ticket Size: ${replaceCurrencies(currency)} ${addCommasToNumber(
                          (advanceTotal / totals.advanceCount).toFixed(2)
                        )} `}
                      />
                    </div>
                  )}
                  {selectedUserType.value2 === "total" &&
                    renderIRRMetrics(irrMonthly, irr12M, irr24M, irr36M, "flex-row gap-6 items-center")}
                  {aggragateOpenList.includes(userProgress.userId) &&
                    renderAggragateTable(userProgress, royalties, recoupedMonthTracker)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const checkContractsOpen = () => {
    return userAdvanceProgressList.length === contractOpenList.length
  }

  const onClickContractAll = () => {
    if (checkContractsOpen()) {
      setContractOpenList([])
    } else {
      const allUserIds = userAdvanceProgressList.map((progress) => progress.userId)
      setContractOpenList(allUserIds)
    }
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      <div className="flex items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"User Advance"} />
          </div>
        </KTooltip>
        <div className="flex gap-2 ml-8">
          <div>
            <KDropdown
              width={90}
              menuWidth={110}
              selected={selectedCurrency}
              options={currencyOptions}
              onSelect={(selected) => setSelectedCurrency(selected as KSelectOption)}
            />
          </div>
          <div>
            <KDropdown
              width={145}
              selected={selectedIRROption}
              options={IRROptionList}
              onSelect={(selected) => setSelectedIRROption(selected as KSelectOption)}
            />
          </div>
          <div>
            <KDropdown
              width={145}
              selected={selectedViewOption}
              options={ViewOptionList}
              onSelect={(selected) => setSelectedViewOption(selected as KSelectOption)}
            />
          </div>
          <div>
            <KDropdown
              width={145}
              selected={selectedSummaryDateType}
              options={RoyaltyDateOptionList}
              onSelect={(selected) => setSelectedSummaryDateType(selected as KSelectOption)}
            />
          </div>
          <div>
            <KDropdown
              width={135}
              selected={selectedAdvanceDateType}
              options={AdvanceDateOptionList}
              onSelect={(selected) => setSelectedAdvanceDateType(selected as KSelectOption)}
            />
          </div>
          <KTooltip
            backgroundColor="#000"
            padding="8px 12px"
            arrowColor="#000"
            position="bottom"
            align="left"
            content={
              <div className="w-64">
                <KSpan
                  text="Total Amount (Gross): Recoupment based on gross royalty until advance is closed"
                  fontSize={11}
                  color="#FFF"
                />
                <div className="mt-2">
                  <KSpan
                    text="Total Net (Artist Share): Recoupment based on artist's share only"
                    fontSize={11}
                    color="#FFF"
                  />
                </div>
              </div>
            }
          >
            <div>
              <KDropdown
                width={180}
                selected={selectedRoyaltyType}
                options={RoyaltyTypeOptionList}
                onSelect={(selected) => setSelectedRoyaltyType(selected as KSelectOption)}
              />
            </div>
          </KTooltip>
          <div>
            <KDropdown
              width={90}
              selected={selectedUserType}
              options={UserTypeOptionList}
              onSelect={(selected) => setSelectedUserType(selected as KSelectOption)}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-4 items-center mb-2">
        <div>
          <KButton
            height="36px"
            background="transparent"
            text={checkContractsOpen() ? "Close Contracts" : "Open Contracts"}
            onClick={() => onClickContractAll()}
            leftIcon="/navigation_icons/profile_dropdownmenu_icons/sign-contract.svg"
          />
        </div>
        {selectedUserType.value2 === "user" && (
          <SearchUserComponent className="" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
        )}
        <div className="flex items-center gap-2 ml-1 mr-1">
          <input
            type="checkbox"
            checked={advancePaidOut}
            onChange={() => setAdvancePaidOut(!advancePaidOut)}
            style={{ accentColor: "black", cursor: "pointer" }}
          />
          <KTooltip
            position="bottom"
            width="220px"
            children={
              <div className="grid">
                <KSpan text="Advance" />
                <KSpan text="Paid Out" />
              </div>
            }
            content={<KSpan text="Fetch advances only if date Advance Paid != Advance Start" />}
          />
        </div>
        <div className="flex items-center gap-2 ml-1 mr-1">
          <input
            type="checkbox"
            checked={showExcluded}
            onChange={() => setShowExcluded(!showExcluded)}
            style={{ accentColor: "black", cursor: "pointer" }}
          />
          <div className="grid">
            <KSpan text="Show" />
            <KSpan text="Excluded" />
          </div>
        </div>
        <div className="flex items-center gap-2 ml-1 mr-1">
          <input
            type="checkbox"
            checked={timelineActive}
            onChange={() => setTimelineActive(!timelineActive)}
            style={{ accentColor: "black", cursor: "pointer" }}
          />
          <div className="grid">
            <KSpan text="Timeline" />
            <KSpan text="Active" />
          </div>
        </div>
        <div>
          <KButton
            text="Apply Filters"
            onClick={() => applyFilters()}
            textColor="white"
            background="black"
            disabled={loading}
          />
        </div>
        <div className="ml-4 flex gap-2">
          <KButton
            text="Export XLSX"
            onClick={() => exportAll()}
            textColor="black"
            background="white"
            disabled={downloadLoading}
          />
        </div>
      </div>
      {renderUserAdvance()}
      {selectedUserType.value2 === "user" && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={onClickPagination}
            display="flex gap-20"
          />
        </div>
      )}
    </div>
  )
}

export default AdminUserAdvance
